<template>
  <v-container>
    <h1>Отчет зарплаты тренеров (персоналки)</h1>
    <div class="mb-5">
      <router-link to="/reports">
        Назад к списку отчётов
      </router-link>
    </div>

    <my-loader :loader="loading"></my-loader>

    <!-- Блок ранее сформированных отчетов -->
    <v-card class="mb-4">
      <v-card-title>Ранее сформированные отчёты</v-card-title>
      <v-btn
        v-if="activeReportId"
        color="primary"
        class="margin-btn"
        small
        @click="activeReportId = null"
      >
        ← Назад к ранее созданным отчетам
      </v-btn>

      <v-card-text>
        <v-progress-circular
          v-if="loading"
          indeterminate
          color="primary"
          size="40"
          class="my-4"
        />

        <!-- Таблица списка отчетов -->
        <v-data-table
          v-if="!loading && !activeReportId"
          :headers="reportsHeaders"
          :items="reportsList"
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:item.created_at="{ item }">
            <a
              href="#"
              @click.prevent="loadReportDetails(item.id)"
            >
              {{ formatDateTime(item.created_at) }}
            </a>
          </template>

          <template v-slot:item.period="{ item }">
            {{ formatDate(item.date_from) }} — {{ formatDate(item.date_to) }}
          </template>

          <template v-slot:item.report_status="{ item }">
            <span class="d-flex align-center">
              {{ getStatusText(item.report_status) }}
              <v-progress-circular
                v-if="item.report_status === 'PROCESSING'"
                indeterminate
                color="primary"
                size="16"
                width="2"
                class="ml-2"
              />
            </span>
          </template>

          <template v-slot:item.action="{ item }">
            <v-btn
              small
              color="success"
              :disabled="item.report_status !== 'READY'"
              @click.stop="exportFullReport(item)"
            >
              Скачать в Excel
            </v-btn>
          </template>
        </v-data-table>

        <!-- Детали отчета -->
        <template v-if="!loading && activeReportId && currentReport">
          <div class="mb-4">
            Отчет {{ currentReport.id }} за период:
            {{ formatDate(currentReport.date_from) }} – {{ formatDate(currentReport.date_to) }}
          </div>

          <v-alert
            v-if="currentReport.report_status === 'PROCESSING'"
            type="info"
          >
            Отчет формируется, пожалуйста, подождите...
          </v-alert>

          <v-alert
            v-else-if="!currentReport.report_json || Object.keys(currentReport.report_json).length === 0"
            type="info"
          >
            Данные отчета отсутствуют
          </v-alert>

          <template v-else>
            <v-data-table
              :headers="reportHeaders"
              :items="currentReport.report_json"
              class="mt-4"
              hide-default-footer
              :items-per-page="-1"
            >
              <template v-slot:item.name="{ item }">
                <a
                  style="cursor: pointer; text-decoration: underline;"
                  @click="showTrainerDetails(item)"
                >
                  {{ item.name }}
                </a>
              </template>

              <template v-slot:item.sessions_count="{ item }">
                {{ item.records.length }}
              </template>

              <template v-slot:item.action="{ item }">
                <v-btn
                  v-if="item.contract"
                  class="ml-5"
                  small
                  color="success"
                  @click="makeSpecification(item)"
                >
                  Создать спецификацию
                </v-btn>
              </template>
            </v-data-table>
          </template>
        </template>
      </v-card-text>
    </v-card>

    <!-- Блок запроса нового отчета -->
    <v-card>
      <v-card-title>Запросить новый отчет</v-card-title>
      <v-card-text>
        <!-- Выбор дат -->
        <v-row>
          <v-col
            cols="12"
            sm="6"
          >
            <v-menu
              v-model="menuFrom"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateFrom"
                  label="Дата начала"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dateFrom"
                @input="menuFrom = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            sm="6"
          >
            <v-menu
              v-model="menuTo"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateTo"
                  label="Дата окончания"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dateTo"
                @input="menuTo = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <v-btn
          color="primary"
          :disabled="!dateFrom || !dateTo"
          :loading="generatingReport"
          @click="createNewReport"
        >
          Сформировать отчет
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Модальное окно с деталями по тренеру -->
    <v-dialog
      v-model="detailsDialog"
      max-width="800"
    >
      <v-card>
        <v-card-title>Детали тренировок: {{ selectedTrainer?.name }}</v-card-title>
        <v-card-text>
          <v-progress-circular
            v-if="loading"
            indeterminate
            color="primary"
            size="40"
            class="my-4"
          />

          <v-data-table
            v-if="!loading"
            :headers="detailsHeaders"
            :items="selectedTrainer?.records || []"
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.start="{ item }">
              {{ formatDateTime(item.start) }}
            </template>

            <template v-slot:item.duration="{ item }">
              {{ formatTrainingDuration(item) }}
            </template>

            <template v-slot:item.split="{ item }">
              {{ item.split ? 'Да' : 'Нет' }}
            </template>

            <template v-slot:item.salary="{ item }">
              {{ item.salary }} ₽
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            @click="detailsDialog = false"
          >
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="salarySpecDialog"
      max-width="800px"
    >
      <v-card>
        <v-card-title>Создание спецификации для {{ selectedCoach?.title }}</v-card-title>
        <v-card-text>
          <!-- Лоадер -->
          <v-progress-circular
            v-if="specLoader"
            indeterminate
            color="primary"
            size="40"
            class="my-4"
          />

          <!-- Выбор ИП -->
          <v-select
            v-if="!specLoader"
            v-model="selectedIp"
            :items="individualsList"
            item-text="name"
            item-value="id"
            label="Выберите ИП"
            outlined
            dense
            class="mb-4"
            @change="loadContractAndSpecs"
          />

          <!-- Номер договора -->
          <div v-if="selectedIp && !specLoader">
            <v-alert
              v-if="contractId"
              type="success"
              dense
            >
              Номер договора: <strong>{{ contract.contract_number }}</strong>
            </v-alert>
            <v-alert
              v-else
              type="error"
              dense
            >
              Отсутствует номер договора у сотрудника с выбранным ИП
            </v-alert>
          </div>

          <!-- Данные спецификации -->
          <div
            v-if="contractId"
            class="mt-3"
          >
            <v-card
              outlined
              class="pa-3"
            >
              <div><strong>Количество тренировок:</strong> {{ selectedCoach?.records.length }}</div>
              <div>
                <strong>Общая сумма:</strong>
                {{ grossFromNet(selectedCoach?.sum, contract) }}
              </div>
            </v-card>
          </div>

          <!-- Последние 3 спецификации -->
          <!-- Последние спецификации -->
          <div
            v-if="salarySpecs.length"
            class="mt-4"
          >
            <h4>Последние спецификации:</h4>
            <v-data-table
              :headers="salarySpecsHeaders"
              :items="salarySpecs.slice(0, 5)"
              dense
              hide-default-footer
            >
              <template v-slot:item.created_at="{ item }">
                {{ item.created_at }}
              </template>

              <template v-slot:item.amount_total="{ item }">
                {{ item.amount_total }} ₽
              </template>

              <template v-slot:item.file_url="{ item }">
                <v-btn
                  v-if="item.file_url"
                  color="success"
                  small
                  :href="item.file_url"
                  target="_blank"
                >
                  Скачать
                </v-btn>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn
                  small
                  color="error"
                  @click="deleteSpec(item)"
                >
                  Удалить
                </v-btn>
              </template>
            </v-data-table>
          </div>

          <v-alert
            v-else-if="selectedIp && !specLoader"
            type="info"
            class="mt-4"
          >
            Спецификаций для выбранного ИП нет.
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="success"
            :disabled="!selectedIp || !contractId"
            @click="createSpec"
          >
            Создать спецификацию
          </v-btn>
          <v-btn
            color="error"
            @click="salarySpecDialog = false"
          >
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Api from '@/api/api'
import MyLoader from '@/components/MyLoader.vue'
import {
  calcTrainerSalary,
  findSalaryEntry,
  formatTrainingDuration,
} from '@/utils/personalTrainingSalary'
import { getSalaryCalcCoefficient, grossFromNet } from '@/utils/salaryCalc'
import * as XLSX from 'xlsx'

export default {
  name: 'PersonalTraining',
  components: {
    MyLoader,
  },

  data() {
    return {
      branchId: Number(this.$route.params.branch_id),
      loading: false,
      generatingReport: false,
      menuFrom: false,
      menuTo: false,
      dateFrom: null,
      dateTo: null,
      reportsList: [],
      currentReport: null,
      activeReportId: null,
      selectedTrainer: null,
      detailsDialog: false,
      pollingInterval: null,
      reportsHeaders: [
        { text: 'Создан', value: 'created_at' },
        { text: 'Период', value: 'period' },
        { text: 'Статус', value: 'report_status' },
        { text: 'Действие', value: 'action', sortable: false },
      ],
      reportHeaders: [
        { text: 'Тренер', value: 'name' },
        { text: 'Категория', value: 'category' },
        { text: 'Кол-во тренировок', value: 'sessions_count' },
        { text: 'Вид спорта', value: 'sport_type' },
        { text: 'Сумма ЗП', value: 'sum' },
        { text: 'Действие', value: 'action' },
      ],
      detailsHeaders: [
        { text: 'Дата тренировки', value: 'start' },
        { text: 'Клиент', value: 'client_name' },
        { text: 'Номер корта', value: 'court_number' },
        { text: 'Длительность', value: 'duration' },
        { text: 'Split', value: 'split' },
        { text: 'Сумма', value: 'salary' },
      ],
      coaches: [],
      salary_dict: [],
      salarySpecDialog: false,
      selectedCoach: [],
      specLoader: false,
      individualsList: [],
      selectedIp: null,
      contractId: null,
      salarySpecs: [],
      salarySpecsHeaders: [
        { text: '№', value: 'spec_number', width: '70px' },
        { text: 'Дата', value: 'created_at', width: '120px' },
        { text: 'Сумма', value: 'amount_total', width: '100px' },
        {
          text: 'Файл', value: 'file_url', sortable: false, width: '80px',
        },
        {
          text: 'Действие', value: 'actions', sortable: false, width: '80px',
        },
      ],
    }
  },

  async created() {
    await this.getStaffByBranchId()
    await this.getSalaryDictPersonal()
    await this.loadReports()
  },

  beforeDestroy() {
    this.stopPolling()
  },

  methods: {
    grossFromNet,
    getSalaryCalcCoefficient,
    formatTrainingDuration,

    enrichReportJson(reportJson) {
      return (reportJson || []).map(trainer => {
        const coachInfo = this.coaches.find(c => c.id === trainer.id)
        const salaryEntry = findSalaryEntry(trainer.id, coachInfo?.category, this.salary_dict)
        const { records, totalSalary } = calcTrainerSalary(trainer, salaryEntry)

        return {
          ...trainer,
          records,
          category: coachInfo?.category || null,
          sport_type: coachInfo?.sport_type || null,
          contract: coachInfo?.contract || null,
          title: coachInfo?.title || null,
          sum: totalSalary,
          total_salary: totalSalary,
        }
      }).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    },

    // Загрузка списка отчетов
    async loadReports() {
      this.loading = true
      try {
        const response = await Api.getPersonalTrainingsReports(this.branchId)
        this.reportsList = response.results || []

        // Если есть отчеты в обработке, запускаем опрос
        this.checkAndStartPolling()
      } catch (error) {
        console.error('Ошибка загрузки отчетов:', error)
        this.$toast.error(error.response?.data?.message || 'Не удалось загрузить список отчетов')
      } finally {
        this.loading = false
      }
    },

    // Проверка и запуск опроса для отчетов в обработке
    checkAndStartPolling() {
      const hasProcessing = this.reportsList.some(r => r.report_status === 'PROCESSING')

      if (hasProcessing && !this.pollingInterval) {
        this.pollingInterval = setInterval(() => {
          this.loadReports()
        }, 10000)
      } else if (!hasProcessing && this.pollingInterval) {
        this.stopPolling()
      }
    },

    // Остановка опроса
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },

    async getStaffByBranchId() {
      this.loader = true
      const params = new URLSearchParams([
        ['branch_id', this.$route.params.branch_id],
        ['report_type', 'personal'],
      ])
      try {
        await Api.getStaffByBranchId({ params }).then(data => {
          this.$set(this, 'loader', false)
          this.$set(this, 'coaches', data)
        })
      } catch (error) {
        console.log(error)
        this.loader = false
      }
    },

    async getSalaryDictPersonal() {
      this.loader = true
      try {
        await Api.getSalaryDictPersonal().then(data => {
          this.$set(this, 'loader', false)
          this.$set(this, 'salary_dict', data)
        })
      } catch (error) {
        console.log(error)
        this.loader = false
      }
    },

    // Загрузка деталей отчета
    async loadReportDetails(reportId) {
      this.activeReportId = reportId
      this.loading = true

      try {
        // Подстраховка — если коучи или зарплаты не загружены, ждём
        if (!this.coaches.length || !this.salary_dict.length) {
          await this.getStaffByBranchId()
          await this.getSalaryDictPersonal()
        }

        const response = await Api.getPersonalTrainingsReport(reportId)

        this.currentReport = {
          ...response,
          report_json: this.enrichReportJson(response.report_json),
        }
      } catch (error) {
        console.error('Ошибка загрузки отчета:', error)
        this.$toast.error(error.response?.data?.message || 'Не удалось загрузить отчет')
      } finally {
        this.loading = false
      }
    },

    async loadReportDetailsForExcel(reportId) {
      this.loading = true

      try {
        // Подстраховка — если коучи или зарплаты не загружены, ждём
        if (!this.coaches.length || !this.salary_dict.length) {
          await this.getStaffByBranchId()
          await this.getSalaryDictPersonal()
        }

        const response = await Api.getPersonalTrainingsReport(reportId)

        this.currentReport = {
          ...response,
          report_json: this.enrichReportJson(response.report_json),
        }
      } catch (error) {
        console.error('Ошибка загрузки отчета:', error)
        this.$toast.error(error.response?.data?.message || 'Не удалось загрузить отчет')
      } finally {
        this.loading = false
      }
    },

    // Создание нового отчета
    async createNewReport() {
      console.log('Sending request with data:', {
        branch_id: this.branchId,
        date_from: this.dateFrom,
        date_to: this.dateTo,
      })

      this.generatingReport = true
      try {
        const response = await Api.createPersonalTrainingsReport({
          branch_id: this.branchId,
          date_from: this.dateFrom,
          date_to: this.dateTo,
        })

        // Упрощенная проверка ответа
        if (response && response.status === 200) {
          await this.loadReports()
        } else {
          throw new Error('Не удалось создать отчет: неверный статус ответа')
        }
      } catch (error) {
        console.error('Ошибка создания отчета:', error)
        const errorMessage = error.response?.data?.message
          || error.message
          || 'Не удалось создать отчет'
        this.$toast.error(errorMessage)
      } finally {
        this.generatingReport = false
      }
    },

    async loadContractAndSpecs() {
      if (!this.selectedIp || !this.selectedCoach) return
      this.specLoader = true
      this.salarySpecs = []
      this.contractId = null
      this.contract = null

      try {
        // Получаем договор
        const contractParams = {
          params: {
            entrepreneur_id: this.selectedIp,
            staff_yc_id: this.selectedCoach.id,
          },
        }
        const contract = await Api.getСontractByIpAndAtaff(contractParams)
        this.contractId = contract?.id || null
        this.contract = contract || null

        // Загружаем последние спецификации только если договор есть
        if (this.contractId) {
          const specsParams = {
            params: {
              company_id: this.$route.params.branch_id,
              counterparty_name: contract.counterparty_name,
              entrepreneur_name: contract.entrepreneur_name,
            },
          }
          this.salarySpecs = await Api.getSalarySpecsListPersonal(specsParams)
        } else {
          this.salarySpecs = []
        }
      } catch (e) {
        console.error(e)
        this.contractId = null
        this.contract = null
        this.salarySpecs = []
      } finally {
        this.specLoader = false
      }
    },

    formatDateOnly(dateString) {
      if (!dateString) return ''

      const [year, month, day] = dateString.split('-').map(Number)
      const date = new Date(year, month - 1, day)

      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },

    // Экспорт полного отчета в Excel
    async exportFullReport(report) {
      try {
        // Загружаем детали отчета, если они еще не загружены
        if (!this.currentReport || this.currentReport.id !== report.id) {
          await this.loadReportDetailsForExcel(report.id)
        }

        if (!this.currentReport?.report_json || Object.keys(this.currentReport.report_json).length === 0) {
          this.$toast.warning('Нет данных для экспорта')
          return
        }

        // Подготовка данных (только уникальные тренеры)
        const data = this.currentReport.report_json.map(trainer => ({
          'Вид спорта': trainer.sport_type || 'Не указан',
          'ФИО тренера': trainer.name,
          'Кол-во тренировок': trainer.records.length,
          'Сумма ЗП': trainer.sum,
        }))

        // Создание рабочей книги
        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet(data)

        // Расчет ширины столбцов
        const columnWidths = Object.keys(data[0]).map(key => {
          const maxLength = data.reduce((max, row) => {
            const val = row[key] ? row[key].toString() : ''
            return Math.max(max, val.length)
          }, key.length)
          return { wch: maxLength + 2 }
        })

        ws['!cols'] = columnWidths

        // Добавление листа в книгу
        XLSX.utils.book_append_sheet(wb, ws, 'Тренеры')

        // Генерация имени файла
        const fileName = `Отчет_тренеров_${this.formatDate(report.date_from)}_${this.formatDate(report.date_to)}.xlsx`

        // Сохранение файла
        XLSX.writeFile(wb, fileName)

        this.$toast.success('Файл успешно скачан')
      } catch (error) {
        console.error('Ошибка при экспорте в Excel:', error)
        this.$toast.error('Не удалось экспортировать данные')
      }
    },

    // Показать детали по тренеру
    showTrainerDetails(trainer) {
      this.selectedTrainer = trainer
      this.detailsDialog = true
    },

    // Форматирование даты
    formatDate(dateString) {
      if (!dateString) return ''

      // Разбиваем YYYY-MM-DD вручную
      const [year, month, day] = dateString.split('-').map(Number)

      // Создаем дату в локальном времени
      const date = new Date(year, month - 1, day)

      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    },

    // Форматирование даты и времени
    formatDateTime(dateString) {
      if (!dateString) return ''

      const [datePart, timePart] = dateString.split('T')
      const [year, month, day] = datePart.split('-').map(Number)

      let hours = 0
      let minutes = 0

      if (timePart) {
        [hours, minutes] = timePart.split(':').map(Number)
      }

      // Создаем дату в локальном времени
      const date = new Date(year, month - 1, day, hours, minutes)

      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    // Текст статуса отчета
    getStatusText(status) {
      switch (status) {
        case 'PROCESSING': return 'Формируется'
        case 'READY': return 'Готов'
        case 'ERROR': return 'Ошибка'
        default: return status
      }
    },

    async createSpec() {
      if (!this.contractId || !this.selectedCoach || !this.selectedIp) {
        alert('Выберите ИП и дождитесь загрузки данных договора')
        return
      }

      try {
        const coefficient = getSalaryCalcCoefficient(this.contract)

        const payload = {
          contract_id: this.contractId,
          training_amount: this.selectedCoach.records.length,
          amount_due: Math.round(this.selectedCoach.sum / this.selectedCoach.records.length / coefficient),
          amount_total: Math.round(this.selectedCoach.sum / coefficient),
          report_id: this.activeReportId,
        }

        await Api.createSalarSpecPersonal(payload)
        alert('Спецификация успешно создана')

        // Обновляем список спецификаций
        const specsParams = {
          params: {
            company_id: this.$route.params.branch_id,
            counterparty_name: this.contract.counterparty_name,
          },
        }
        this.salarySpecs = await Api.getSalarySpecsListPersonal(specsParams)

        // Автоматическое скачивание последнего файла
        if (this.salarySpecs.length > 0) {
          const lastSpec = this.salarySpecs[0]
          if (lastSpec.file_url) {
            window.open(lastSpec.file_url, '_blank')
          }
        }
      } catch (e) {
        console.error(e)
        let message = 'Ошибка при создании спецификации'

        // Проверка разных форматов ошибок
        if (e.response?.data) {
          const { data } = e.response
          if (typeof data === 'string') {
            message = data
          } else if (Array.isArray(data)) {
            message = data.join('\n')
          } else if (typeof data === 'object') {
            // Берём первое значение из объекта
            const firstKey = Object.keys(data)[0]
            if (Array.isArray(data[firstKey])) {
              message = data[firstKey].join('\n')
            } else {
              message = String(data[firstKey])
            }
          }
        }

        alert(message)
      }
    },
    async makeSpecification(coach) {
      console.log(coach)
      this.selectedCoach = coach
      this.salarySpecDialog = true
      this.specLoader = true
      this.individualsList = []
      this.salarySpecs = []
      this.selectedIp = null
      this.contractId = null
      this.contract = null

      try {
        const params = { params: { branch_id: this.$route.params.branch_id } }
        this.individualsList = await Api.getIndividualsList(params)
      } catch (e) {
        console.error(e)
        alert('Ошибка при загрузке списка ИП')
      } finally {
        this.specLoader = false
      }
    },
    async deleteSpec(spec) {
      const confirmDelete = confirm(`Удалить спецификацию №${spec.spec_number} от ${spec.created_at}?`)
      if (!confirmDelete) return

      try {
        const payload = {
          spec_id: spec.id,
        }
        await Api.deleteSalarSpecPersonal({
          params: { spec_id: spec.id },
        })
        alert('Спецификация удалена')

        // Обновляем список после удаления
        const specsParams = {
          params: {
            company_id: this.$route.params.branch_id,
            counterparty_name: this.contract.counterparty_name,
            entrepreneur_name: this.contract.entrepreneur_name,
          },
        }
        this.salarySpecs = await Api.getSalarySpecsListPersonal(specsParams)
      } catch (e) {
        console.error(e)
        alert('Ошибка при удалении спецификации')
      }
    },
  },
}
</script>

<style scoped>
.margin-btn {
  margin-left: 20px;
  margin-bottom: 10px;
}
</style>
